import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  TrendingUp, 
  Shield,
  PiggyBank,
  CreditCard,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  BookOpen,
  Target,
  Users
} from "lucide-react";

/**
 * Financial Health Overview Component
 * 
 * Displays a comprehensive view of the user's financial health with
 * actionable insights and educational recommendations
 */

// Mock financial health data - replace with real financial metrics
const mockFinancialHealth = {
  overallScore: 75,
  categories: {
    emergencyFund: {
      score: 60,
      current: 1500,
      target: 4000,
      status: "building",
      insight: "You're on track! Add $200/month to reach your 6-month emergency fund goal.",
      lessonRecommendation: "Emergency Fund Essentials"
    },
    budgetCompliance: {
      score: 85,
      current: 78, // percentage of budget followed
      target: 80,
      status: "excellent",
      insight: "Great job staying within budget! You're consistently making smart spending choices.",
      lessonRecommendation: null
    },
    debtManagement: {
      score: 92,
      current: 8, // debt-to-income ratio
      target: 10,
      status: "excellent",
      insight: "Your debt-to-income ratio is healthy. Consider increasing investment contributions.",
      lessonRecommendation: "Investment Basics"
    },
    savingsRate: {
      score: 70,
      current: 15, // percentage of income saved
      target: 20,
      status: "good",
      insight: "Good savings rate! Consider automating an extra 5% to reach the recommended 20%.",
      lessonRecommendation: "Automated Savings Strategies"
    }
  },
  recommendations: [
    {
      priority: "high",
      title: "Boost Emergency Fund",
      description: "Increase monthly savings by $200 to reach 6-month emergency fund",
      potentialImpact: "+15 Centse Score points",
      lesson: "Emergency Fund Planning"
    },
    {
      priority: "medium", 
      title: "Optimize Investment Allocation",
      description: "With low debt, consider increasing investment contributions to 15%",
      potentialImpact: "+10 Centse Score points",
      lesson: "Portfolio Diversification"
    },
    {
      priority: "low",
      title: "Review Insurance Coverage",
      description: "Ensure adequate protection for your growing assets",
      potentialImpact: "+5 Centse Score points",
      lesson: "Insurance Fundamentals"
    }
  ]
};

const FinancialHealthOverview = () => {
  const { overallScore, categories, recommendations } = mockFinancialHealth;

  // Get status color and icon
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "excellent":
        return { 
          color: "text-green-600 bg-green-50 border-green-200", 
          icon: CheckCircle,
          text: "Excellent"
        };
      case "good":
        return { 
          color: "text-blue-600 bg-blue-50 border-blue-200", 
          icon: TrendingUp,
          text: "Good"
        };
      case "building":
        return { 
          color: "text-yellow-600 bg-yellow-50 border-yellow-200", 
          icon: Target,
          text: "Building"
        };
      default:
        return { 
          color: "text-red-600 bg-red-50 border-red-200", 
          icon: AlertTriangle,
          text: "Needs Attention"
        };
    }
  };

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-800 border-red-200";
      case "medium": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "low": return "bg-green-100 text-green-800 border-green-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="space-y-6">
      {/* Overall Financial Health Score */}
      <Card className="bg-gradient-to-br from-secondary/5 to-secondary/10 border-secondary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-secondary" />
              <CardTitle className="text-lg font-semibold text-gray-900">
                Financial Health Score
              </CardTitle>
            </div>
            <Badge variant="secondary" className="bg-secondary/20 text-secondary">
              Updated Today
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-4xl font-bold text-secondary mb-1">{overallScore}/100</div>
              <p className="text-sm text-gray-600">
                {overallScore >= 80 ? "Excellent financial health!" : 
                 overallScore >= 60 ? "Good progress, keep it up!" : 
                 "Building healthy financial habits"}
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-white shadow-sm flex items-center justify-center border-4 border-secondary/20">
                <TrendingUp className="h-8 w-8 text-secondary" />
              </div>
            </div>
          </div>
          
          {/* Score Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Overall Health</span>
              <span>{overallScore}%</span>
            </div>
            <Progress value={overallScore} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {/* Financial Categories Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(categories).map(([key, category]) => {
          const statusDisplay = getStatusDisplay(category.status);
          const StatusIcon = statusDisplay.icon;
          
          return (
            <Card key={key} className="bg-background border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Category Header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {key === "emergencyFund" && <PiggyBank className="h-4 w-4 text-secondary" />}
                      {key === "budgetCompliance" && <Target className="h-4 w-4 text-secondary" />}
                      {key === "debtManagement" && <CreditCard className="h-4 w-4 text-secondary" />}
                      {key === "savingsRate" && <DollarSign className="h-4 w-4 text-secondary" />}
                      <h3 className="font-medium text-gray-900 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </h3>
                    </div>
                    <Badge variant="outline" className={`${statusDisplay.color} border text-xs`}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {statusDisplay.text}
                    </Badge>
                  </div>

                  {/* Score and Progress */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Score</span>
                      <span className="font-medium">{category.score}/100</span>
                    </div>
                    <Progress value={category.score} className="h-2" />
                  </div>

                  {/* Current vs Target */}
                  <div className="text-sm text-gray-600">
                    {key === "emergencyFund" && (
                      <span>${category.current.toLocaleString()} of ${category.target.toLocaleString()} goal</span>
                    )}
                    {key === "budgetCompliance" && (
                      <span>{category.current}% budget compliance (target: {category.target}%)</span>
                    )}
                    {key === "debtManagement" && (
                      <span>{category.current}% debt-to-income ratio (target: under {category.target}%)</span>
                    )}
                    {key === "savingsRate" && (
                      <span>{category.current}% savings rate (target: {category.target}%)</span>
                    )}
                  </div>

                  {/* Insight */}
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                    💡 {category.insight}
                  </p>

                  {/* Learn More Button */}
                  {category.lessonRecommendation && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full bg-secondary/5 border-secondary/20 hover:bg-secondary/10"
                    >
                      <BookOpen className="h-3 w-3 mr-2" />
                      Learn: {category.lessonRecommendation}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Smart Recommendations */}
      <Card className="bg-background border-gray-200 shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-secondary" />
            <CardTitle className="text-lg font-semibold text-gray-900">
              Personalized Recommendations
            </CardTitle>
          </div>
          <p className="text-sm text-gray-600">
            AI-powered suggestions to improve your financial health
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recommendations.map((rec, index) => (
              <div 
                key={index}
                className="p-4 border border-gray-100 rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium text-gray-900">{rec.title}</h4>
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${getPriorityColor(rec.priority)} border`}
                      >
                        {rec.priority} priority
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{rec.description}</p>
                    <p className="text-xs text-secondary font-medium">{rec.potentialImpact}</p>
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="bg-secondary/5 border-secondary/20 hover:bg-secondary/10"
                >
                  <BookOpen className="h-3 w-3 mr-2" />
                  Learn: {rec.lesson}
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinancialHealthOverview;